/**
 * Khoảng thời gian của bộ lọc nhật ký thao tác (UI_RECONSTRUCTION_SPEC §8.2,
 * hợp đồng triển khai mục 6: `from`/`to`, mặc định 24 giờ, tối đa 90 ngày).
 *
 * Hàm thuần: nhận `bayGio` (ms) thay vì đọc đồng hồ, nên mọi ranh giới kiểm
 * được trực tiếp. "Hôm nay" tính theo giờ Việt Nam (UTC+7, không có giờ mùa hè).
 */

export type MocThoiGian = 'hom_nay' | '24_gio' | '7_ngay' | '30_ngay' | 'tuy_chon';

export const MOC_THOI_GIAN: readonly MocThoiGian[] = [
  'hom_nay',
  '24_gio',
  '7_ngay',
  '30_ngay',
  'tuy_chon',
];

/** Trần khoảng lọc của audit-service. */
export const TRAN_BO_LOC_NGAY = 90;
/** Số dòng mỗi trang. */
export const GIOI_HAN_TRANG = 50;

const GIO = 3_600_000;
const NGAY = 24 * GIO;
const LECH_VN = 7 * GIO;

export interface Khoang {
  from: string;
  to: string;
}

export type KetQuaKhoang =
  | { ok: true; khoang: Khoang }
  | { ok: false; loi: 'thieu_moc' | 'nguoc' | 'qua_dai'; toiDa?: number };

/** 00:00 giờ Việt Nam của ngày chứa `bayGio`. */
export function dauNgayVN(bayGio: number): number {
  return Math.floor((bayGio + LECH_VN) / NGAY) * NGAY - LECH_VN;
}

/**
 * Dựng khoảng từ mốc chọn sẵn, hoặc kiểm khoảng tuỳ chọn.
 *
 * `7_ngay` là 7 × 24 giờ lùi từ bây giờ, không phải "từ 00:00 bảy ngày trước" —
 * đúng nghĩa đặc tả ghi cho bộ lọc này.
 */
export function dungKhoang(
  moc: MocThoiGian,
  bayGio: number,
  tuyChon?: { tu: number | null; den: number | null },
): KetQuaKhoang {
  const iso = (n: number) => new Date(n).toISOString();
  switch (moc) {
    case 'hom_nay':
      return { ok: true, khoang: { from: iso(dauNgayVN(bayGio)), to: iso(bayGio) } };
    case '24_gio':
      return { ok: true, khoang: { from: iso(bayGio - NGAY), to: iso(bayGio) } };
    case '7_ngay':
      return { ok: true, khoang: { from: iso(bayGio - 7 * NGAY), to: iso(bayGio) } };
    case '30_ngay':
      return { ok: true, khoang: { from: iso(bayGio - 30 * NGAY), to: iso(bayGio) } };
    case 'tuy_chon': {
      const tu = tuyChon?.tu ?? null;
      const den = tuyChon?.den ?? null;
      if (tu === null || den === null) return { ok: false, loi: 'thieu_moc' };
      if (den <= tu) return { ok: false, loi: 'nguoc' };
      if (den - tu > TRAN_BO_LOC_NGAY * NGAY) {
        return { ok: false, loi: 'qua_dai', toiDa: TRAN_BO_LOC_NGAY };
      }
      return { ok: true, khoang: { from: iso(tu), to: iso(den) } };
    }
  }
}
